<template>
  <div class="tcla-dashboard">
    <div class="tcla-cards-grid">
      <div class="tcla-card">
        <div class="tcla-card__icon">🧭</div>
        <div class="tcla-card__value">{{ dashboardStats.avgAuthority }}</div>
        <div class="tcla-card__label">اقتدار میانگین</div>
      </div>
      <div class="tcla-card">
        <div class="tcla-card__icon">⚓</div>
        <div class="tcla-card__value">{{ dashboardStats.avgAnchor }}</div>
        <div class="tcla-card__label">تنوع انکر میانگین</div>
      </div>
      <div class="tcla-card tcla-card--warn" v-if="analysis.orphans.length">
        <div class="tcla-card__icon">🏝️</div>
        <div class="tcla-card__value">{{ analysis.orphans.length }}</div>
        <div class="tcla-card__label">صفحات یتیم</div>
      </div>
      <div class="tcla-card tcla-card--danger" v-if="analysis.deadEnds.length">
        <div class="tcla-card__icon">🚧</div>
        <div class="tcla-card__value">{{ analysis.deadEnds.length }}</div>
        <div class="tcla-card__label">صفحات بن‌بست</div>
      </div>
    </div>

    <div class="tcla-panel">
      <h3 class="tcla-panel__title">🏅 صفحات برتر بر اساس امتیاز SEO</h3>
      <div v-if="topPages.length" class="tcla-score-table">
        <div class="tcla-score-row tcla-score-row--head">
          <span>صفحه</span>
          <span>امتیاز</span>
          <span>اقتدار</span>
          <span>لینک‌ها</span>
        </div>
        <div v-for="page in topPages" :key="page.url" class="tcla-score-row">
          <span class="tcla-score-row__title">{{ page.title }}</span>
          <span class="tcla-score-pill" :class="scoreClass(page.score)">{{ page.score }}</span>
          <span>{{ page.authority }}</span>
          <span>{{ page.linkCount }}</span>
        </div>
      </div>
      <div v-else class="tcla-empty">هیچ صفحه‌ای برای نمایش نیست</div>
    </div>

    <div class="tcla-panel">
      <h3 class="tcla-panel__title">📊 خوشه‌های موضوعی</h3>
      <div v-if="analysis.topicalClusters.length" class="tcla-cluster-list">
        <div v-for="cluster in analysis.topicalClusters" :key="cluster.name" class="tcla-cluster-card">
          <div class="tcla-cluster-card__header">
            <span class="tcla-cluster-card__name">{{ cluster.name }}</span>
            <span class="tcla-cluster-card__badge">{{ cluster.pageCount }} صفحه</span>
          </div>
          <div class="tcla-cluster-card__meta">اقتدار: {{ cluster.avgAuthority }}</div>
        </div>
      </div>
      <div v-else class="tcla-empty">هیچ خوشه‌ای یافت نشد</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TCLADashboard',
  props: {
    analysis: {
      type: Object,
      required: true
    }
  },
  computed: {
    dashboardStats() {
      const scores = Object.values(this.analysis.pageScores || {});
      const authorities = Object.values(this.analysis.pageRank?.normalized || {});
      const anchors = (this.analysis.anchorAnalysis || []).map(a => a.diversityScore);
      
      const avg = (arr) => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : 0;
      
      return {
        avgAuthority: avg(authorities),
        avgAnchor: avg(anchors)
      };
    },
    topPages() {
      return Object.entries(this.analysis.pageScores || {})
        .map(([url, score]) => ({
          url,
          title: this.analysis.graph?.nodes?.[url]?.title || url,
          score: score.seoScore,
          authority: score.authorityScore,
          linkCount: (this.analysis.graph?.nodes?.[url]?.incomingLinks?.length || 0) + 
                     (this.analysis.graph?.nodes?.[url]?.outgoingLinks?.length || 0)
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
    }
  },
  methods: {
    scoreClass(score) {
      if (score >= 70) return 'tcla-score--good';
      if (score >= 40) return 'tcla-score--mid';
      return 'tcla-score--bad';
    }
  }
};
</script>

<style scoped>
.tcla-dashboard { margin-bottom: 20px; }

.tcla-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

.tcla-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.tcla-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.tcla-card__icon { font-size: 1.4rem; margin-bottom: 8px; }
.tcla-card__value { font-size: 1.8rem; font-weight: 700; color: #1f2937; margin-bottom: 4px; }
.tcla-card__label { font-size: 0.75rem; color: #9ca3af; font-weight: 500; }

.tcla-card--warn .tcla-card__value { color: #f59e0b; }
.tcla-card--danger .tcla-card__value { color: #ef4444; }

.tcla-score-table { display: flex; flex-direction: column; gap: 1px; font-size: 0.8rem; }
.tcla-score-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
}
.tcla-score-row--head { font-weight: 700; color: #9ca3af; font-size: 0.72rem; text-transform: uppercase; }
.tcla-score-row__title { text-align: right; font-weight: 600; overflow: hidden; text-overflow: ellipsis; }

.tcla-score-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.75rem;
}
.tcla-score--good { background: #d1fae5; color: #16a34a; }
.tcla-score--mid { background: #fef3c7; color: #d97706; }
.tcla-score--bad { background: #fee2e2; color: #ef4444; }

.tcla-cluster-list { display: flex; flex-direction: column; gap: 8px; }
.tcla-cluster-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 12px 14px;
  background: #f9fafb;
}
.tcla-cluster-card__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.tcla-cluster-card__name { font-weight: 600; color: #1f2937; }
.tcla-cluster-card__badge { font-size: 0.75rem; background: #dbeafe; color: #3b82f6; padding: 2px 8px; border-radius: 100px; }
.tcla-cluster-card__meta { font-size: 0.75rem; color: #9ca3af; }

.tcla-empty { text-align: center; padding: 20px; color: #9ca3af; }

.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  margin-bottom: 16px;
}

.tcla-panel__title { font-size: 0.95rem; font-weight: 700; margin: 0 0 14px 0; }
</style>
